import {
  MiddleLinksConfigType,
  MAX_NUM_LINKS,
  MAX_NUM_MIDDLE_LINKS,
  DEFAULT_FIRST_MIDDLE_VAL,
  PaginationProps,
  EllipsisPositions,
} from '../components/Pagination';

/**
 * Determines which ellipsis are required to be displayed
 * and returns either none, both, left or right
 *
 * @param {number} current The currently active link
 * @param {number} pages The number of pages in the range
 * @return {string} Either 'none', 'both', 'left' or 'right'
 */
export const determineEllipsisLocation = (
  current: PaginationProps['current'],
  pages: PaginationProps['pages'],
): EllipsisPositions => {
  const showLeftEllipsis = current > MAX_NUM_LINKS - 1 && pages > MAX_NUM_LINKS;
  const showRightEllipsis = current < pages - MAX_NUM_MIDDLE_LINKS;

  if (showLeftEllipsis && showRightEllipsis) {
    return 'both';
  } else if (showLeftEllipsis && !showRightEllipsis) {
    return 'left';
  } else if (!showLeftEllipsis && showRightEllipsis) {
    return 'right';
  }
  return 'none';
};

/**
 * Determines the start value of the middle links (of which
 * there can be a maximum of 3) and the number of links to show.
 *
 * @param {string} showEllipsis Either 'none', 'both', 'left' or 'right'
 * @param {number} current The currently active link
 * @param {number} pages The number of pages in the range
 * @return {array} The first element is the start value in the middle links
 * and the second element is the number of middle links to show (max 3)
 */
export const determineMiddleLinksConfig = (
  showEllipsis: EllipsisPositions,
  current: PaginationProps['current'],
  pages: PaginationProps['pages'],
): MiddleLinksConfigType => {
  if (pages < 2) return [0, 0];
  switch (showEllipsis) {
    case 'none':
    case 'right':
      return [
        DEFAULT_FIRST_MIDDLE_VAL,
        pages - MAX_NUM_MIDDLE_LINKS > DEFAULT_FIRST_MIDDLE_VAL
          ? MAX_NUM_MIDDLE_LINKS
          : pages - (MAX_NUM_LINKS - MAX_NUM_MIDDLE_LINKS),
      ];
    case 'left':
      return [pages - MAX_NUM_MIDDLE_LINKS, MAX_NUM_MIDDLE_LINKS];
    default:
      return [current - 1, MAX_NUM_MIDDLE_LINKS];
  }
};
